// The settle handle for the human-idle hard DELETE, kept in its own module
// rather than in `useWsSession` for the same reason `session-status.ts` is: the
// surfaces' tests mock the whole `useWsSession` module, so anything exported
// from there vanishes under every such mock — and this one is awaited on the
// resume path, where an `undefined` import reads as a failed wake.
//
// The race it closes: the idle kill fires `DELETE /sessions/:id?reason=idle`
// and does not await it. A resume issued inside that round trip lands
// `/reconnect` first, triggers a pipeline, and then the DELETE arrives behind
// it, removes the session from the live registry and cancels the job just paid
// for — leaving a session no sandbox ever boots into, with no error anywhere.
// The window is a hairline (a keystroke cancels the countdown, so the user has
// to press one within milliseconds of the kill), but it is silent and
// unrecoverable, so a resume waits the DELETE out.

const settled = new Map<string, Promise<void>>();

/** Record this session's in-flight idle DELETE. Never rejects. */
export const trackIdleDelete = (
  sessionId: string,
  deletion: Promise<unknown>,
): void => {
  settled.set(
    sessionId,
    deletion.then(
      () => undefined,
      () => undefined,
    ),
  );
};

/** Forget it — the session has been resumed, so nothing is in flight. */
export const clearIdleDelete = (sessionId: string): void => {
  settled.delete(sessionId);
};

/** Resolve once this session's idle DELETE has settled; immediately if none. */
export const awaitIdleDelete = (sessionId: string): Promise<void> =>
  settled.get(sessionId) ?? Promise.resolve();
